import type {
  GameState,
  MapDefinition,
  PlayerId,
  Territory,
  TerritoryId,
} from '../types.js';
import { DEFAULT_PERSONALITY, type AiPersonality } from './personality.js';
import {
  type ScoringContext,
  adjustedAttackEv,
} from './scoring.js';

/**
 * Pure scoring helpers used by `chooseAction`. Everything here is
 * deterministic — ties are broken by territory id ordering so tests can
 * pin down expected choices without seeded RNG.
 *
 * As of v0.5 these wrap `adjustedAttackEv` from scoring.ts, which adds
 * threat weighting (anti-kingmaker), elimination bonuses, region
 * completion/denial, and personality aggression bias on top of the raw
 * single-roll dice EV.
 */

export interface AttackChoice {
  readonly from: TerritoryId;
  readonly to: TerritoryId;
  readonly ev: number; // adjusted EV (post threat / elim / region / bias)
}

/**
 * Look up the player's personality from `state.players`, falling back
 * to DEFAULT_PERSONALITY (cautious) when missing. Lets test-built
 * states without explicit personalities still drive the AI sensibly.
 */
function personalityOf(
  state: GameState,
  player: PlayerId
): AiPersonality {
  const p = state.players.find((x) => x.id === player);
  return p?.personality ?? DEFAULT_PERSONALITY;
}

function buildContext(
  state: GameState,
  map: MapDefinition,
  player: PlayerId
): ScoringContext {
  return { state, map, player, personality: personalityOf(state, player) };
}

export interface FortifyChoice {
  readonly from: TerritoryId;
  readonly to: TerritoryId;
  readonly score: number; // enemy pressure on destination
}

function adjacencyMap(
  map: MapDefinition
): ReadonlyMap<TerritoryId, readonly TerritoryId[]> {
  return new Map(map.territories.map((t) => [t.id, t.adjacent]));
}

function ownedTerritories(
  state: GameState,
  player: PlayerId
): readonly TerritoryId[] {
  const ids: TerritoryId[] = [];
  for (const [id, ts] of Object.entries(state.territories)) {
    if (ts.owner === player) ids.push(id as TerritoryId);
  }
  ids.sort();
  return ids;
}

/**
 * Look up team affiliation for any owner. Defensive — falls back to
 * a sentinel if the owner isn't in `state.players` (shouldn't happen
 * at runtime, but the BFS / scoring code crosses tile-owner edges
 * where validation hasn't run yet).
 */
function teamOf(state: GameState, ownerId: PlayerId): number {
  const p = state.players.find((x) => x.id === ownerId);
  return p?.team ?? -1;
}

/** True iff `ownerId` is on `player`'s team (or is `player`). */
function isTeammate(
  state: GameState,
  ownerId: PlayerId,
  player: PlayerId
): boolean {
  if (ownerId === player) return true;
  return teamOf(state, ownerId) === teamOf(state, player);
}

/**
 * Sum of units across opposing-team tiles adjacent to `terrId`. Higher
 * = more under threat. Teammate-owned tiles don't contribute (they're
 * not a threat in team play). Used to pick deploy targets and
 * fortify destinations.
 */
export function enemyPressure(
  state: GameState,
  map: MapDefinition,
  terrId: TerritoryId,
  owner: PlayerId
): number {
  const t = map.territories.find((x) => x.id === terrId);
  if (!t) return 0;
  let pressure = 0;
  for (const n of t.adjacent) {
    const ts = state.territories[n];
    if (!ts) continue;
    if (isTeammate(state, ts.owner, owner)) continue;
    pressure += ts.units;
  }
  return pressure;
}

function hasEnemyNeighbor(
  state: GameState,
  territory: Territory,
  owner: PlayerId
): boolean {
  for (const n of territory.adjacent) {
    const ts = state.territories[n];
    if (!ts) continue;
    if (!isTeammate(state, ts.owner, owner)) return true;
  }
  return false;
}

/**
 * Pick the most-pressured owned tile for the player. If the player has
 * no border tiles (rare — mid-game island scenario), falls back to the
 * first owned tile by id. Never returns null while the player owns at
 * least one tile.
 */
export function mostPressuredOwned(
  state: GameState,
  map: MapDefinition,
  player: PlayerId
): TerritoryId | null {
  const owned = ownedTerritories(state, player);
  if (owned.length === 0) return null;

  let bestId: TerritoryId | null = null;
  let bestScore = -1;
  for (const id of owned) {
    const score = enemyPressure(state, map, id, player);
    if (score > bestScore) {
      bestScore = score;
      bestId = id;
    }
  }
  // If every owned tile has pressure 0 (no enemy neighbors anywhere),
  // bestScore stays at 0 after first iteration and we just take the
  // alphabetically-first owned tile — fine for setup deploy.
  return bestId ?? owned[0]!;
}

/**
 * Enumerate all favorable attacks: every (from, to) where the AI owns
 * `from` with ≥2 units, `to` is an adjacent enemy tile, and the
 * adjusted EV exceeds `personality.attackEvThreshold`. Returns the
 * top `k` ranked descending by adjusted EV.
 *
 * Used by `chooseAction` to feed top-K softmax sampling so AI play
 * isn't always the single strictest optimum. Tie-breaking is stable
 * (territory id alphabetical) so the ranking is deterministic given
 * the state.
 */
export function topAttacks(
  state: GameState,
  map: MapDefinition,
  player: PlayerId,
  k: number
): readonly AttackChoice[] {
  if (k <= 0) return [];
  const ctx = buildContext(state, map, player);
  const candidates: AttackChoice[] = [];
  for (const t of [...map.territories].sort((a, b) =>
    a.id < b.id ? -1 : a.id > b.id ? 1 : 0
  )) {
    const fromState = state.territories[t.id];
    if (!fromState) continue;
    if (fromState.owner !== player) continue;
    if (fromState.units < 2) continue;

    for (const nId of [...t.adjacent].sort()) {
      const toState = state.territories[nId];
      if (!toState) continue;
      // Skip teammates AND self — friendly fire is invalid in team
      // play. The validator would reject the action anyway, but
      // filtering here means the AI never even considers it.
      if (isTeammate(state, toState.owner, player)) continue;
      const ev = adjustedAttackEv(ctx, fromState.units, toState.units, nId);
      if (ev <= ctx.personality.attackEvThreshold) continue;
      candidates.push({ from: t.id, to: nId, ev });
    }
  }
  candidates.sort((a, b) => b.ev - a.ev);
  return candidates.slice(0, k);
}

/**
 * Single best attack — a thin wrapper over `topAttacks` returning
 * just the top candidate (or null). Preserved for callers / tests
 * that want deterministic single-best behavior without sampling.
 */
export function bestAttack(
  state: GameState,
  map: MapDefinition,
  player: PlayerId
): AttackChoice | null {
  const top = topAttacks(state, map, player, 1);
  return top[0] ?? null;
}

export interface DeployChoice {
  readonly id: TerritoryId;
  readonly ev: number; // post-deploy best-neighbor adjusted EV
}

/**
 * Enumerate offensive deploy candidates: for each owned border tile,
 * simulate stacking all reinforcements there and compute the best
 * adjusted EV against any enemy neighbor. Returns the top `k`
 * candidates whose post-deploy EV beats the personality threshold.
 *
 * Tiles with no enemy neighbors are skipped (they can't be attack
 * sources). When the list is empty the caller should fall back to
 * defensive deploy via `mostPressuredOwned`.
 */
export function topDeployTargets(
  state: GameState,
  map: MapDefinition,
  player: PlayerId,
  reinforcements: number,
  k: number
): readonly DeployChoice[] {
  if (k <= 0) return [];
  const owned = ownedTerritories(state, player);
  if (owned.length === 0) return [];
  const ctx = buildContext(state, map, player);

  const candidates: DeployChoice[] = [];
  for (const id of owned) {
    const t = map.territories.find((x) => x.id === id);
    if (!t) continue;
    const ts = state.territories[id];
    if (!ts) continue;

    const hypothetical = ts.units + reinforcements;
    let bestNeighborEv = Number.NEGATIVE_INFINITY;
    for (const nId of t.adjacent) {
      const nState = state.territories[nId];
      if (!nState) continue;
      if (isTeammate(state, nState.owner, player)) continue;
      const ev = adjustedAttackEv(ctx, hypothetical, nState.units, nId);
      if (ev > bestNeighborEv) bestNeighborEv = ev;
    }
    if (bestNeighborEv === Number.NEGATIVE_INFINITY) continue;
    if (bestNeighborEv <= ctx.personality.attackEvThreshold) continue;
    candidates.push({ id, ev: bestNeighborEv });
  }
  candidates.sort((a, b) => b.ev - a.ev);
  return candidates.slice(0, k);
}

/**
 * Pick where to deploy this turn's reinforcements.
 *
 * Two-tier strategy:
 *  1. **Offensive tier**: pick the highest-EV deploy candidate that
 *     beats `personality.attackEvThreshold`. The AI is investing in
 *     offense.
 *  2. **Defensive fallback**: if no tile produces a favorable attack
 *     even after reinforcement, fall back to `mostPressuredOwned` —
 *     the AI shores up its weakest border instead of throwing units
 *     away.
 *
 * Single-best convenience wrapper around `topDeployTargets`. For the
 * sampling-aware path see `chooseAction`, which calls
 * `topDeployTargets` directly with k=3.
 */
export function deployTarget(
  state: GameState,
  map: MapDefinition,
  player: PlayerId,
  reinforcements: number
): TerritoryId | null {
  const top = topDeployTargets(state, map, player, reinforcements, 1);
  if (top.length > 0) return top[0]!.id;
  // No favorable offensive deploy — defend the weakest border.
  return mostPressuredOwned(state, map, player);
}

/**
 * Find all owned tiles with no enemy neighbors and ≥2 units — candidates
 * to drain into a threatened border via fortify.
 */
function interiorSources(
  state: GameState,
  map: MapDefinition,
  player: PlayerId
): readonly Territory[] {
  const out: Territory[] = [];
  for (const t of map.territories) {
    const ts = state.territories[t.id];
    if (!ts) continue;
    if (ts.owner !== player) continue;
    if (ts.units < 2) continue;
    if (hasEnemyNeighbor(state, t, player)) continue;
    out.push(t);
  }
  out.sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  return out;
}

/**
 * BFS from `from` through tiles owned by anyone on `player`'s team,
 * returning each reachable team-tile (including `from` itself). The
 * returned set may include teammate-owned tiles; callers that need
 * to filter to player-owned destinations do so themselves.
 */
function reachableTeam(
  state: GameState,
  map: MapDefinition,
  from: TerritoryId,
  player: PlayerId
): ReadonlySet<TerritoryId> {
  const me = state.players.find((p) => p.id === player);
  if (!me) return new Set([from]);
  const adj = adjacencyMap(map);
  const teamByOwner = new Map(state.players.map((p) => [p.id, p.team]));
  const visited = new Set<TerritoryId>([from]);
  const queue: TerritoryId[] = [from];
  while (queue.length > 0) {
    const here = queue.shift()!;
    for (const n of adj.get(here) ?? []) {
      if (visited.has(n)) continue;
      const ts = state.territories[n];
      if (!ts) continue;
      if (teamByOwner.get(ts.owner) !== me.team) continue;
      visited.add(n);
      queue.push(n);
    }
  }
  return visited;
}

/**
 * Decide how many units to advance into a freshly conquered tile.
 *
 * Default behavior (and current shipped behavior) is to push everything
 * forward. That's optimal when the source tile has no remaining enemy
 * neighbors — there's nothing left to attack from there, so units stay
 * stranded. But when the source has OTHER enemies adjacent (i.e. the
 * conquered tile wasn't its only attack vector), draining it to the
 * minimum gives up a free follow-up offensive on the same turn. This
 * heuristic reserves a small stack at source in that case.
 *
 * Reserve size is personality-tuned:
 *   - Aggressive: keep 1 (leans forward, only barely holds back)
 *   - Cautious:   keep 2 (preserves a 1-die attack option)
 *   - Wildcard:   1..3 randomized via the supplied prng
 *
 * Clamped to [minUnits, maxUnits] so the game-rule floor (must advance
 * at least as many dice as you rolled) is always respected.
 */
export function bestAdvance(
  state: GameState,
  map: MapDefinition,
  player: PlayerId,
  sub: { from: TerritoryId; to: TerritoryId; minUnits: number; maxUnits: number },
  rollInt: (max: number) => number
): number {
  const fromTerr = map.territories.find((t) => t.id === sub.from);
  if (!fromTerr) return sub.maxUnits;

  // Count enemy neighbors of source OTHER than the one we just took.
  // Teammate tiles never count (no friendly fire); the just-conquered
  // tile is now ours; only fresh enemies make holding back worthwhile.
  let otherEnemies = 0;
  for (const n of fromTerr.adjacent) {
    if (n === sub.to) continue;
    const ts = state.territories[n];
    if (!ts) continue;
    if (isTeammate(state, ts.owner, player)) continue;
    otherEnemies++;
  }
  if (otherEnemies === 0) return sub.maxUnits;

  const personality = personalityOf(state, player);
  let keepBack: number;
  if (personality.id === 'aggressive') {
    keepBack = 1;
  } else if (personality.id === 'wildcard') {
    keepBack = 1 + rollInt(3); // 1..3
  } else {
    keepBack = 2; // cautious / default
  }

  // sub.maxUnits = source.units - 1. We end source with `keepBack`, so
  // advance = source.units - keepBack = maxUnits - keepBack + 1.
  const desiredAdvance = sub.maxUnits - keepBack + 1;
  return Math.max(sub.minUnits, Math.min(sub.maxUnits, desiredAdvance));
}

/**
 * Best (from, to) fortify pair: pick a safe interior source and route
 * its surplus to the connected border tile under the most enemy
 * pressure. Returns null if there's no useful move (no interior tiles,
 * or interior tiles aren't connected to any border).
 */
export function bestFortify(
  state: GameState,
  map: MapDefinition,
  player: PlayerId
): FortifyChoice | null {
  const sources = interiorSources(state, map, player);
  if (sources.length === 0) return null;

  let best: FortifyChoice | null = null;
  for (const src of sources) {
    // Reachability walks through teammate space, but the destination
    // tile must still be owned by `player` — units don't get
    // deposited in an ally's territory.
    const reach = reachableTeam(state, map, src.id, player);
    for (const destId of [...reach].sort()) {
      if (destId === src.id) continue;
      const destState = state.territories[destId];
      if (!destState || destState.owner !== player) continue;
      const destTerr = map.territories.find((t) => t.id === destId);
      if (!destTerr) continue;
      if (!hasEnemyNeighbor(state, destTerr, player)) continue;
      const score = enemyPressure(state, map, destId, player);
      if (score <= 0) continue;
      if (best === null || score > best.score) {
        best = { from: src.id, to: destId, score };
      }
    }
  }
  return best;
}
